import { DateTime } from "luxon";

import {
  IProductionDashboardTimelineEntry,
  VehicleStationHistoryReason,
} from "../models/ProductionDashboard";

export interface ITimelineWindow {
  /** Start of the visible day, local time. */
  start: DateTime;
  /** End of the visible day, local time. */
  end: DateTime;
}

/** One station spell inside a vehicle's bar, positioned relative to the bar (0–100). */
export interface ITimelineSegment {
  partAssemblyId: string;
  name: string | null;
  leftPercent: number;
  widthPercent: number;
  isRunning: boolean;
  isManual: boolean;
  startLabel: string;
  endLabel: string;
}

/** One vehicle's bar on a bay row: first spell start to last spell end (or now). */
export interface ITimelineBlock {
  key: string;
  vehicleId: string;
  vrm: string | null;
  label: string;
  /** Position and size as percentages of the track width. */
  leftPercent: number;
  widthPercent: number;
  isRunning: boolean;
  isComplete: boolean;
  segments: ITimelineSegment[];
}

// Default working window. Stretched, never shrunk, so work outside it is still on screen.
const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 17;

const local = (iso: string): DateTime => DateTime.fromISO(iso, { zone: "utc" }).toLocal();

/**
 * The visible span of the timeline: 07:00 to 17:00 local, widened to take in anything worked
 * earlier or later and the current time, rounded out to whole hours so the tick marks stay clean.
 */
export function timelineWindow(
  entries: IProductionDashboardTimelineEntry[],
  now: DateTime
): ITimelineWindow {
  const dayStart = now.startOf("day");
  let start = dayStart.set({ hour: DEFAULT_START_HOUR });
  let end = dayStart.set({ hour: DEFAULT_END_HOUR });

  const times = entries.flatMap((e) => {
    const t = [local(e.startedOn)];
    if (e.endedOn) t.push(local(e.endedOn));
    return t;
  });
  times.push(now);

  for (const t of times) {
    if (t < start) start = t.startOf("hour");
    if (t > end) end = t.plus({ hours: 1 }).startOf("hour");
  }

  return { start, end };
}

const percentBetween = (t: DateTime, from: DateTime, to: DateTime): number => {
  const span = to.toMillis() - from.toMillis();
  if (span <= 0) return 0;
  const p = ((t.toMillis() - from.toMillis()) / span) * 100;
  return Math.min(100, Math.max(0, p));
};

const percentOf = (t: DateTime, window: ITimelineWindow): number =>
  percentBetween(t, window.start, window.end);

/**
 * Groups the station spells of each vehicle into one bar per vehicle, in first-start order. A
 * spell still open runs to `now` and marks the bar as running. Segments are positioned relative
 * to their bar so the component can nest them without re-doing the maths.
 */
export function timelineBlocks(
  entries: IProductionDashboardTimelineEntry[],
  window: ITimelineWindow,
  now: DateTime
): ITimelineBlock[] {
  const byVehicle = new Map<string, IProductionDashboardTimelineEntry[]>();
  for (const e of entries) {
    const list = byVehicle.get(e.vehicleId) ?? [];
    list.push(e);
    byVehicle.set(e.vehicleId, list);
  }

  return Array.from(byVehicle.entries())
    .map(([vehicleId, spells]) => {
      const sorted = [...spells].sort((a, b) => a.startedOn.localeCompare(b.startedOn));
      const first = sorted[0];
      const start = local(first.startedOn);
      const isRunning = sorted.some((s) => !s.endedOn);
      const end = isRunning
        ? now
        : sorted.reduce((latest, s) => {
            const t = local(s.endedOn as string);
            return t > latest ? t : latest;
          }, start);

      const left = percentOf(start, window);
      const right = percentOf(end, window);

      const segments: ITimelineSegment[] = sorted.map((s) => {
        const sStart = local(s.startedOn);
        const sEnd = s.endedOn ? local(s.endedOn) : now;
        const sLeft = percentBetween(sStart, start, end);
        const sRight = percentBetween(sEnd, start, end);
        return {
          partAssemblyId: s.partAssemblyId,
          name: s.partAssemblyName,
          leftPercent: sLeft,
          widthPercent: Math.max(0, sRight - sLeft),
          isRunning: !s.endedOn,
          isManual: s.source === VehicleStationHistoryReason.Manual,
          startLabel: sStart.toFormat("HH:mm"),
          endLabel: s.endedOn ? sEnd.toFormat("HH:mm") : "",
        };
      });

      return {
        key: `${vehicleId}-${first.startedOn}`,
        vehicleId,
        vrm: first.vrm,
        label: [first.combinedMake, first.combinedModel].filter(Boolean).join(" "),
        leftPercent: left,
        // A floor so a bar that started a moment ago is still visible.
        widthPercent: Math.max(0.5, right - left),
        isRunning,
        isComplete: sorted.some((s) => s.isComplete),
        segments,
      };
    })
    .sort((a, b) => a.leftPercent - b.leftPercent);
}

export function timelineTicks(window: ITimelineWindow): { label: string; leftPercent: number }[] {
  const ticks: { label: string; leftPercent: number }[] = [];
  for (let t = window.start; t <= window.end; t = t.plus({ hours: 1 })) {
    ticks.push({ label: t.toFormat("HH:mm"), leftPercent: percentOf(t, window) });
  }
  return ticks;
}

export const nowPercent = (window: ITimelineWindow, now: DateTime): number => percentOf(now, window);
